<script setup lang="ts">
// ponytail: unified cross-ecosystem admin sidebar for bot dashboards (prod URLs, no i18n, no .env)
type AdminAppId =
  | 'stream'
  | 'users'
  | 'warframe'
  | 'skins-wf'
  | 'theorycraft-wf'
  | 'data-wf'
  | 'pandaahhdmin'
  | 'stream-chat'
  | 'bot-riven'

const props = defineProps<{
  current: AdminAppId
}>()

const user = useSanctumUser<Record<string, any>>()
const isDiscordAdmin = useState<boolean | null>('auth:isDiscordAdmin', () => null)

const isOpen = shallowRef(false)
const collapsed = shallowRef(false)

const normalizedRoles = computed(() => {
  const u = user.value
  if (!u) return [] as string[]
  const raw = [
    ...(Array.isArray(u.roles) ? u.roles : []),
    ...(Array.isArray(u.warframe?.roles) ? u.warframe.roles : []),
    ...(Array.isArray(u.skins?.roles) ? u.skins.roles : []),
    ...(Array.isArray(u.theorycraft?.roles) ? u.theorycraft.roles : []),
    ...(Array.isArray(u.theorycraftProfile?.roles) ? u.theorycraftProfile.roles : [])
  ]
  return raw.map((r) => String(r).toUpperCase().trim())
})

const appAdmins = computed(() => {
  const raw = user.value?.appAdmin || user.value?.app_admin
  return Array.isArray(raw) ? raw.map((a) => String(a).toLowerCase().trim()) : []
})

const isSuperAdmin = computed(() =>
  Boolean(
    user.value?.superAdmin ||
    user.value?.super_admin ||
    normalizedRoles.value.includes('SUPER_ADMIN')
  )
)

const hasAppAdmin = (...keys: string[]) => keys.some((k) => appAdmins.value.includes(k))

const hasRivenRole = computed(() =>
  normalizedRoles.value.some((r) => ['BOT RIVEN', 'BOT-RIVEN', 'BOT_RIVEN', 'BOT_RIVENS'].includes(r))
)

const hasDataRole = computed(() =>
  normalizedRoles.value.some((r) => ['ADMIN DATA WF', 'ADMIN_DATA_WF', 'DATA_WF', 'DATA-WF'].includes(r))
)

const adminSection = computed(() => {
  const entries = [
    {
      id: 'stream' as const,
      label: 'Stream',
      icon: 'i-heroicons-tv',
      to: 'https://pandaahh.com/admin/overview',
      visible: isSuperAdmin.value
    },
    {
      id: 'users' as const,
      label: 'Users',
      icon: 'i-heroicons-user-group',
      to: 'https://pandaahh.com/users/admin/users',
      visible: isSuperAdmin.value
    },
    {
      id: 'warframe' as const,
      label: 'Warframe',
      icon: 'i-heroicons-globe-alt',
      to: 'https://pandaahh.com/warframe/fr/admin/overview',
      visible: isSuperAdmin.value || hasAppAdmin('warframe')
    },
    {
      id: 'skins-wf' as const,
      label: 'Fashionframe',
      icon: 'i-fa6-solid-shirt',
      to: 'https://pandaahh.com/warframe/fashionframe/fr/admin/overview',
      visible: isSuperAdmin.value || hasAppAdmin('skins', 'fashionframe')
    },
    {
      id: 'theorycraft-wf' as const,
      label: 'Theorycraft',
      icon: 'i-heroicons-cpu-chip',
      to: 'https://pandaahh.com/warframe/theorycraft/fr/admin/overview',
      visible: isSuperAdmin.value || hasAppAdmin('theorycraft', 'warframe')
    },
    {
      id: 'data-wf' as const,
      label: 'Data Warframe',
      icon: 'i-heroicons-circle-stack',
      to: 'https://pandaahh.com/warframe/data',
      visible:
        isSuperAdmin.value ||
        hasAppAdmin('data-wf', 'warframe', 'bot-riven', 'bot_riven') ||
        hasDataRole.value ||
        hasRivenRole.value
    }
  ].filter((item) => item.visible)

  if (!entries.length) return []

  return [
    { label: 'Admins', type: 'label' as const },
    ...entries.map((item) => ({
      label: item.label,
      icon: item.icon,
      to: item.to,
      active: false,
      class: 'cursor-pointer'
    }))
  ]
})

const botSection = computed(() => {
  const entries = [
    {
      id: 'pandaahhdmin' as const,
      label: 'Pandaahhdmin',
      icon: 'i-fa6-brands-discord',
      to: props.current === 'pandaahhdmin' ? '/' : 'https://pandaahhdmin.pandaahh.com',
      visible:
        isSuperAdmin.value ||
        hasAppAdmin('pandaahhdmin') ||
        isDiscordAdmin.value === true ||
        props.current === 'pandaahhdmin'
    },
    {
      id: 'stream-chat' as const,
      label: 'Stream Chat',
      icon: 'i-heroicons-chat-bubble-left-right',
      to: props.current === 'stream-chat' ? '/' : 'https://stream-chat.pandaahh.com',
      visible: isSuperAdmin.value || props.current === 'stream-chat'
    },
    {
      id: 'bot-riven' as const,
      label: 'Bot Riven',
      icon: 'i-heroicons-sparkles',
      to: props.current === 'bot-riven' ? '/' : 'https://bot-riven.pandaahh.com',
      visible:
        isSuperAdmin.value ||
        hasAppAdmin('bot-riven', 'bot_riven') ||
        hasRivenRole.value ||
        isDiscordAdmin.value === true ||
        props.current === 'bot-riven'
    }
  ].filter((item) => item.visible)

  if (!entries.length) return []

  return [
    { label: 'Bots', type: 'label' as const },
    ...entries.map((item) => ({
      label: item.label,
      icon: item.icon,
      to: item.to,
      active: item.id === props.current,
      class: 'cursor-pointer'
    }))
  ]
})

const toolSection = computed(() => {
  if (!isSuperAdmin.value) return []

  const tools = [
    { label: 'Umami', icon: 'i-heroicons-chart-pie', to: 'https://analytics.pandaahh.com' },
    { label: 'Uptime Kuma', icon: 'i-heroicons-signal', to: 'https://status.pandaahh.com/status/admin' },
    { label: 'GlitchTip', icon: 'i-heroicons-bug-ant', to: 'https://errors.pandaahh.com' },
    { label: 'Beszel', icon: 'i-heroicons-server', to: 'https://monitoring.pandaahh.com' },
    { label: 'Dozzle', icon: 'i-heroicons-command-line', to: 'https://logs.pandaahh.com' },
    { label: 'Dockge', icon: 'i-fa6-brands-docker', to: 'https://docker.pandaahh.com' },
    { label: 'MinIO Console', icon: 'i-heroicons-cloud', to: 'https://console-cdn.pandaahh.com' }
  ]

  return [
    { label: 'Tools', type: 'label' as const },
    ...tools.map((tool) => ({
      ...tool,
      target: '_blank',
      class: 'cursor-pointer'
    }))
  ]
})

const sections = computed(() =>
  [adminSection.value, botSection.value, toolSection.value].filter((s) => s.length > 0)
)
</script>

<template>
  <UButton
    icon="i-heroicons-squares-2x2"
    color="neutral"
    variant="ghost"
    class="lg:hidden fixed top-4 left-4 z-50 cursor-pointer"
    aria-label="Écosystème"
    @click="isOpen = true"
  />

  <UDashboardSidebar
    id="ecosystem"
    v-model:open="isOpen"
    v-model:collapsed="collapsed"
    collapsible
    :ui="{
      header: 'min-h-(--ui-admin-header-height) h-auto! px-3 w-full flex-row-reverse lg:flex-row border-b border-default/60'
    }"
    class="bg-[#0B1220]/95 backdrop-blur-md border-r border-default shadow-xl shrink-0"
    :class="collapsed ? 'lg:w-16! lg:min-w-16! lg:max-w-16!' : 'lg:w-56! lg:min-w-56! lg:max-w-56!'"
  >
    <template #toggle="{ toggle }">
      <UButton
        icon="i-lucide-x"
        color="neutral"
        variant="ghost"
        class="lg:hidden cursor-pointer"
        @click="toggle"
      />
    </template>

    <template #header="{ collapsed: isCol, collapse }">
      <div
        class="flex items-center w-full py-4 transition-all"
        :class="isCol ? 'flex-col justify-center gap-3' : 'flex-row justify-between gap-2'"
      >
        <div class="flex items-center gap-2 min-w-0">
          <div class="flex items-center justify-center size-8 rounded-lg bg-primary/15 text-primary shrink-0">
            <UIcon name="i-heroicons-squares-2x2" class="size-4.5" />
          </div>
          <span v-if="!isCol" class="font-bold text-sm tracking-wide uppercase truncate">
            Écosystème
          </span>
        </div>

        <UButton
          :icon="isCol ? 'i-lucide-panel-left-open' : 'i-lucide-panel-left-close'"
          color="neutral"
          variant="ghost"
          size="sm"
          class="hidden lg:inline-flex cursor-pointer shrink-0"
          @click="collapse?.(!isCol)"
        />
      </div>
    </template>

    <template #default="{ collapsed: isCol }">
      <template v-for="(section, idx) in sections" :key="idx">
        <USeparator v-if="idx > 0" class="my-1.5" />
        <UNavigationMenu
          :collapsed="isCol"
          :tooltip="isCol"
          :items="section"
          orientation="vertical"
        />
      </template>
    </template>
  </UDashboardSidebar>
</template>
